
import Vue from 'vue'
import axios from 'axios'
import { EventBus } from '@/plugins/eventBus'

interface Position {
  id: number
  name: string
  priority: number | null
  totalDelegated: number
  campaign_count: number
  answer_count_yes: number
  answer_count_no: number
}

interface Client {
  id: number
  name: string
  positions?: Position[]
}

interface PositionWithClient extends Position {
  clientName: string
  distance: number | null
}

interface Action {
  text: string
  value: string
}

export default Vue.extend({
  name: 'WorkerPositionsTab',
  props: {
    clients: {
      type: Array as () => Client[],
      default: () => []
    },
    actions: {
      type: Array as () => Action[],
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    workerId: {
      type: Number,
      required: true
    },
    headers: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: true
    },
    showDistance: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchText: '',
      selected: [] as PositionWithClient[],
      currentPageItems: [] as PositionWithClient[],
      action: this.actions[0]?.value || '',
      actionOnItem: {} as Record<number, string>,
      selectedPage: false,
      pageIndeterminate: false,
      allSelected: false,
      allIndeterminate: false,
      defaultAction: this.actions[0]?.value || ''
    }
  },
  computed: {
    calculatedPositions(): PositionWithClient[] {
      const positions = this.clients.flatMap(client =>
        client.positions?.map(position => ({
          ...position,
          clientName: client.name,
          distance: this.getDistance((position as PositionWithClient).distance)
        })) || []
      )

      // Initialize actionOnItem with the default action for each new position
      positions.forEach(position => {
        if (!(position.id in this.actionOnItem)) {
          this.$set(this.actionOnItem, position.id, this.defaultAction)
        }
      })
      
      return positions
    },
    filteredPositions(): PositionWithClient[] {
      if (this.showDistance) {
        return this.calculatedPositions.filter(item =>
          item.distance !== null &&
          item.distance !== undefined && (
            item.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
            item.clientName.toLowerCase().includes(this.searchText.toLowerCase())
          )
        )
      } else {
        return this.calculatedPositions.filter(item =>
          item.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          item.clientName.toLowerCase().includes(this.searchText.toLowerCase())
        )
      }
    }
  },
  methods: {
    getDistance(distance: number | null) {
      if (distance === null || distance === undefined) {
        return null
      }
      return distance
    },
    updateCurrentItems(items: PositionWithClient[]) {
      this.currentPageItems = items
      this.updateSelectionState()
    },
    updateSelectionState() {
      if (!this.currentPageItems.length) {
        this.selectedPage = false
        this.pageIndeterminate = false
        return
      }

      const selectedIds = new Set(this.selected.map(item => item.id))
      const pageSelectedCount = this.currentPageItems.filter(item => selectedIds.has(item.id)).length

      this.selectedPage = pageSelectedCount === this.currentPageItems.length
      this.pageIndeterminate = pageSelectedCount > 0 && pageSelectedCount < this.currentPageItems.length

      const totalSelectedCount = this.selected.length
      this.allSelected = totalSelectedCount === this.calculatedPositions.length
      this.allIndeterminate = totalSelectedCount > 0 && totalSelectedCount < this.calculatedPositions.length
    },
    selectPage() {
      if (this.selectedPage) {
        this.selected = this.selected.filter(item => 
          !this.currentPageItems.some(pageItem => pageItem.id === item.id)
        )
        this.selectedPage = false
        this.pageIndeterminate = false
      } else {
        const newSelected = [...this.selected]
        this.currentPageItems.forEach(item => {
          if (!newSelected.some(selected => selected.id === item.id)) {
            newSelected.push(item)
          }
        })
        this.selected = newSelected
        this.selectedPage = true
        this.pageIndeterminate = false
      }
      this.updateSelectionState()
    },
    selectAllFilteredItems() {
      if (this.allSelected) {
        this.selected = []
        this.allSelected = false
        this.allIndeterminate = false
      } else {
        // We filter the items by name or client name
        const filteredItems = this.calculatedPositions.filter(item =>
          item.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          item.clientName.toLowerCase().includes(this.searchText.toLowerCase())
        )
        
        // We create a new array with the selected items
        const newSelected = [...this.selected]
        
        // We add the new filtered items if they are not already present
        filteredItems.forEach(item => {
          if (!newSelected.some(selected => selected.id === item.id)) {
            newSelected.push(item)
          }
        })
        
        this.selected = newSelected
        this.allSelected = this.selected.length === this.calculatedPositions.length
        this.allIndeterminate = this.selected.length > 0 && this.selected.length < this.calculatedPositions.length
      }
      this.updateSelectionState()
    },
    performAction() {
      const payload = {
        [this.workerId]: { [this.action]: this.selected.map(item => item.id) }
      }

      axios
        .put('/v1/workers', payload)
        .then(() => {
          EventBus.$emit('snackbar', {
            message: this.$vuetify.lang.t(`$vuetify.${this.action}_success`),
            color: 'success'
          })
          this.$emit('refresh')
        })
        .catch(error => {
          EventBus.$emit('snackbar', { axiosError: error })
        })
    },
    performActionOne(item: PositionWithClient) {
      const selectedAction = this.actionOnItem[item.id] || this.defaultAction
      const payload = {
        [this.workerId]: { [selectedAction]: [item.id] }
      }

      axios
        .put('/v1/workers', payload)
        .then(() => {
          EventBus.$emit('snackbar', {
            message: this.$vuetify.lang.t(`$vuetify.${selectedAction}_success`),
            color: 'success'
          })
          this.$emit('refresh')
        })
        .catch(error => {
          EventBus.$emit('snackbar', { axiosError: error })
        })
    }
  }
})
